import type {
  AgentView,
  AgentVersionView,
  ApprovalDetail,
  ApprovalSummary,
  AdminWorkspace,
  ChatMessage,
  ConnectionTestResult,
  ConnectionView,
  DailyCostBreakdown,
  WorkspaceDashboard,
  PlatformStats,
  GoogleConnectStartResult,
  GoogleConnectStatus,
  HubspotConnectStartResult,
  HubspotConnectStatus,
  DispatchConnectStatus,
  PitchreplyConnectStatus,
  FakeEmailConnectStatus,
  GoogleLoginStartResult,
  GoogleLoginStatus,
  CreateAgentRequest,
  InviteRequest,
  LoginRequest,
  MeResponse,
  NotificationView,
  RegisterRequest,
  RunDetail,
  RunStepsPage,
  RunSummary,
  SessionResponse,
  VersionConfig,
  VersionInfo,
  PollerConfig,
  PollerConfigUpdate,
  PollerRunsResponse,
  PollerRunDetailResponse,
  PollerDecisionsResponse,
  PollerConnectionHealthResponse,
  AdminUserSearchResponse,
  AdminConnectionsResponse,
} from '../contracts';
import { POLLER_RUNS_DEFAULT_LIMIT } from '../contracts';

/**
 * Typed API client (Master Spec §14, §18.3 WP-10). The ONE place the web app talks
 * to the server: it wraps `fetch`, injects the bearer token, unwraps the standard
 * `{ data, meta }` envelope, and throws a typed `ApiError` on `{ error }`. Every
 * method consumes the shared Zod-derived types (`@app/shared`) — no endpoint invents
 * its own request/response variant (§8.2). Mobile (M4) reuses this same client.
 *
 * It is deliberately runtime-agnostic (`fetch` + a token getter/setter injected by
 * the host), so tests and the web share one implementation.
 */

export interface Envelope<T> {
  data: T;
  meta?: PaginationMeta;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

export interface ApiClientOptions {
  /** Base URL for the API, e.g. '' (same-origin) or 'http://localhost:3000'. */
  baseUrl?: string;
  tokenStore: TokenStore;
  fetchImpl?: typeof fetch;
}

interface Query {
  [k: string]: string | number | boolean | undefined;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl ?? '';
    this.tokenStore = opts.tokenStore;
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  get token(): string | null {
    return this.tokenStore.get();
  }

  setToken(token: string | null): void {
    this.tokenStore.set(token);
  }

  private url(path: string, query?: Query): string {
    const qs = query
      ? Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return `${this.baseUrl}${path}${qs ? `?${qs}` : ''}`;
  }

  private async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; query?: Query } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {};
    const token = this.tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

    const res = await this.fetchImpl(this.url(path, opts.query), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const json: unknown = text ? JSON.parse(text) : {};

    if (!res.ok) {
      const err = (json as { error?: ApiErrorBody }).error;
      throw new ApiError(
        res.status,
        err?.code ?? 'UNKNOWN',
        err?.message ?? `Request failed (${res.status})`,
        err?.details,
      );
    }
    return json as T;
  }

  private async get<T>(path: string, query?: Query): Promise<Envelope<T>> {
    return this.request<Envelope<T>>('GET', path, { query });
  }
  private async post<T>(path: string, body?: unknown): Promise<Envelope<T>> {
    return this.request<Envelope<T>>('POST', path, { body });
  }
  private async put<T>(path: string, body?: unknown): Promise<Envelope<T>> {
    return this.request<Envelope<T>>('PUT', path, { body });
  }
  private async patch<T>(path: string, body?: unknown): Promise<Envelope<T>> {
    return this.request<Envelope<T>>('PATCH', path, { body });
  }
  /** DELETE returning 204/No Content — no envelope to unwrap. */
  private async del(path: string): Promise<void> {
    await this.request<void>('DELETE', path);
  }

  // ── Version (public build provenance, feature/version-footer) ─────────────
  /**
   * Public (no-auth) build provenance of the running deployment — the short git
   * SHA + ISO-8601 UTC image-build time baked into the image. Both are `null` in
   * local dev (nothing baked); the web footer then shows a quiet "local dev".
   */
  async getVersion(): Promise<VersionInfo> {
    return (await this.get<VersionInfo>('/api/version')).data;
  }

  // ── Admin: global poller config (operator-gated, v1.13.0) ─────────────────
  /**
   * Read the GLOBAL email-poller configuration (the single platform-level loop's
   * operator knobs). Operator-gated: the server 403s a non-operator caller. Not
   * workspace-scoped — there is exactly one config for the whole deployment.
   */
  async getPollerConfig(): Promise<PollerConfig> {
    return (await this.get<PollerConfig>('/api/admin/poller-config')).data;
  }
  /**
   * Replace the GLOBAL email-poller configuration (operator-gated). The body carries
   * all knobs (a full replace); the server validates + persists it, refreshes its
   * process cache and notifies other processes to refresh, then returns the saved config.
   */
  async updatePollerConfig(body: PollerConfigUpdate): Promise<PollerConfig> {
    return (await this.put<PollerConfig>('/api/admin/poller-config', body)).data;
  }
  /**
   * Read the BOUNDED history of recent email-poller ticks (operator-gated, v1.13.5). Returns
   * the newest-first runs plus a `summary` (current config + last-run instant) for the UI
   * headline. `limit` is clamped server-side (default 20, max 100). Non-operator → 403.
   */
  async getPollerRuns(limit: number = POLLER_RUNS_DEFAULT_LIMIT): Promise<PollerRunsResponse> {
    return (await this.get<PollerRunsResponse>('/api/admin/poller-runs', { limit })).data;
  }
  /**
   * Per-user RUN DETAIL drill-down for one poller tick (operator-gated, #316): the tick +
   * its per-connection fetch rollups + that tick's per-email decisions. Non-operator → 403.
   */
  async getPollerRunDetail(id: string): Promise<PollerRunDetailResponse> {
    return (await this.get<PollerRunDetailResponse>(`/api/admin/poller-runs/${encodeURIComponent(id)}`))
      .data;
  }
  /**
   * The per-email decision lookup (operator-gated, #316) for the Poller Activity screen. Every
   * filter is optional; `email` resolves server-side to the reporting user's workspaces/mailboxes.
   * Newest-first, clamped server-side (default 50, max 200). Non-operator → 403.
   */
  async getPollerDecisions(params: {
    workspaceId?: string;
    connectionId?: string;
    email?: string;
    decision?: string;
    reason?: string;
    since?: string;
    limit?: number;
  } = {}): Promise<PollerDecisionsResponse> {
    return (await this.get<PollerDecisionsResponse>('/api/admin/poller-decisions', params)).data;
  }
  /**
   * Connection fetch-health drill-down (operator-gated, #316): the durable poll cursor
   * position(s) for one connection plus its recent per-tick fetch rollups (newest-first), so an
   * operator can see "we never fetched this mailbox". Non-operator → 403.
   */
  async getPollerConnectionHealth(
    connectionId: string,
    limit?: number,
  ): Promise<PollerConnectionHealthResponse> {
    return (
      await this.get<PollerConnectionHealthResponse>(
        `/api/admin/poller-connections/${encodeURIComponent(connectionId)}/health`,
        { limit },
      )
    ).data;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────
  async register(body: RegisterRequest): Promise<SessionResponse> {
    const res = await this.post<SessionResponse>('/api/auth/register', body);
    this.tokenStore.set(res.data.token);
    return res.data;
  }
  async login(body: LoginRequest): Promise<SessionResponse> {
    const res = await this.post<SessionResponse>('/api/auth/login', body);
    this.tokenStore.set(res.data.token);
    return res.data;
  }
  async logout(): Promise<void> {
    try {
      await this.request<void>('POST', '/api/auth/logout');
    } finally {
      this.tokenStore.set(null);
    }
  }

  // ── Sign in with Google (OIDC login, #56) ──────────────────────────────────
  /** Whether the "Sign in with Google" button should be shown (503-free probe). */
  async googleLoginStatus(): Promise<GoogleLoginStatus> {
    return (await this.get<GoogleLoginStatus>('/api/auth/google/status')).data;
  }
  /** Begin the Google login flow — returns the authorize URL + CSRF state to redirect to. */
  async startGoogleLogin(): Promise<GoogleLoginStartResult> {
    return (await this.post<GoogleLoginStartResult>('/api/auth/google/start')).data;
  }
  /**
   * Finish the Google login flow with the `code`+`state` from the redirect. On
   * success the server returns a session; we store the token exactly like password
   * login so the app is authenticated.
   */
  async completeGoogleLogin(code: string, state: string): Promise<SessionResponse> {
    const res = await this.post<SessionResponse>('/api/auth/google/callback', { code, state });
    this.tokenStore.set(res.data.token);
    return res.data;
  }
  async me(): Promise<MeResponse> {
    return (await this.get<MeResponse>('/api/auth/me')).data;
  }
  async invite(
    workspaceId: string,
    body: InviteRequest,
  ): Promise<{ userId: string; role: string }> {
    return (
      await this.post<{ userId: string; role: string }>(
        `/api/workspaces/${workspaceId}/members`,
        body,
      )
    ).data;
  }

  // ── Agents ──────────────────────────────────────────────────────────────────
  async listAgents(workspaceId: string, query?: Query): Promise<Paginated<AgentView>> {
    return (await this.get<AgentView[]>(`/api/workspaces/${workspaceId}/agents`, query)) as Paginated<AgentView>;
  }
  async getAgent(workspaceId: string, agentId: string): Promise<AgentView> {
    return (await this.get<AgentView>(`/api/workspaces/${workspaceId}/agents/${agentId}`)).data;
  }
  async createAgent(workspaceId: string, body: CreateAgentRequest): Promise<AgentView> {
    return (await this.post<AgentView>(`/api/workspaces/${workspaceId}/agents`, body)).data;
  }
  /**
   * Rename an agent (#294). The name is the one mutable field on an agent — its `kind`,
   * versions and config are immutable. Owner-gated server-side; returns the updated
   * `AgentView` (with the new name) so callers can reflect it immediately.
   */
  async renameAgent(workspaceId: string, agentId: string, name: string): Promise<AgentView> {
    return (
      await this.patch<AgentView>(`/api/workspaces/${workspaceId}/agents/${agentId}`, { name })
    ).data;
  }
  async publishVersion(
    workspaceId: string,
    agentId: string,
    config: VersionConfig,
  ): Promise<{ version: AgentVersionView; triggerCount: number; scheduleCount: number }> {
    return (
      await this.post<{ version: AgentVersionView; triggerCount: number; scheduleCount: number }>(
        `/api/workspaces/${workspaceId}/agents/${agentId}/versions`,
        { config },
      )
    ).data;
  }
  async listVersions(
    workspaceId: string,
    agentId: string,
    query?: Query,
  ): Promise<Paginated<AgentVersionView>> {
    return (await this.get<AgentVersionView[]>(
      `/api/workspaces/${workspaceId}/agents/${agentId}/versions`,
      query,
    )) as Paginated<AgentVersionView>;
  }
  async updateAgentStatus(
    workspaceId: string,
    agentId: string,
    // `archive`/`unarchive` (v1.11.0, #215) are the reversible hide/show pair.
    action: 'pause' | 'disable' | 'resume' | 'archive' | 'unarchive',
  ): Promise<AgentView> {
    return (
      await this.post<AgentView>(`/api/workspaces/${workspaceId}/agents/${agentId}/status`, {
        action,
      })
    ).data;
  }
  /**
   * Fire an agent manually (§12.1). An optional per-run `taskPrompt` (#155) is
   * honoured only by a `templated` agent — the server persists it on the run and
   * derives its label; a `static` agent ignores it. Omit it for a default fire.
   */
  async triggerAgent(
    workspaceId: string,
    agentId: string,
    taskPrompt?: string,
  ): Promise<{ eventId: string; runId: string }> {
    return (
      await this.post<{ eventId: string; runId: string }>(
        `/api/workspaces/${workspaceId}/agents/${agentId}/trigger`,
        taskPrompt === undefined ? undefined : { taskPrompt },
      )
    ).data;
  }

  // ── Runs (§14 operations) ─────────────────────────────────────────────────
  /**
   * List runs. Optional server-side filters (#130): `agentId`, and an inclusive
   * `createdAt` window as ISO timestamps (`createdAfter`/`createdBefore`). All compose
   * with pagination.
   */
  async listRuns(workspaceId: string, query?: Query): Promise<Paginated<RunSummary>> {
    return (await this.get<RunSummary[]>(`/api/workspaces/${workspaceId}/runs`, query)) as Paginated<RunSummary>;
  }
  async getRun(workspaceId: string, runId: string): Promise<RunDetail> {
    return (await this.get<RunDetail>(`/api/workspaces/${workspaceId}/runs/${runId}`)).data;
  }
  /** Keyset page of transcript steps (§14) — lazy-loads the rest of a long transcript. */
  async getRunSteps(
    workspaceId: string,
    runId: string,
    query?: { after?: number; limit?: number },
  ): Promise<RunStepsPage> {
    return (await this.get<RunStepsPage>(`/api/workspaces/${workspaceId}/runs/${runId}/steps`, query))
      .data;
  }
  async cancelRun(workspaceId: string, runId: string): Promise<RunSummary> {
    return (await this.post<RunSummary>(`/api/workspaces/${workspaceId}/runs/${runId}/cancel`)).data;
  }
  /** Delete a terminal run + its children (#130). 204 No Content; 409 if non-terminal. */
  async deleteRun(workspaceId: string, runId: string): Promise<void> {
    await this.del(`/api/workspaces/${workspaceId}/runs/${runId}`);
  }

  // ── Usage / cost analytics (§14) ──────────────────────────────────────────
  /**
   * Per-day cost breakdown, stacked by agent, over the last `days` calendar days
   * (UTC; default 14, capped server-side). Backs the Runs-page cost chart. Cost is
   * authoritative on `usage_records.cost_usd` (#195); Quick-chat (null-agent) cost
   * folds into its own bucket so the daily totals are complete.
   */
  async getDailyCost(workspaceId: string, days?: number): Promise<DailyCostBreakdown> {
    return (
      await this.get<DailyCostBreakdown>(`/api/workspaces/${workspaceId}/usage/daily-cost`, {
        days,
      })
    ).data;
  }

  // ── Dashboard / stats (§14) ───────────────────────────────────────────────
  /**
   * The workspace home dashboard: agent counts, run totals/status/time-series, the top
   * agents by run volume, and the daily cost breakdown (same shape as `getDailyCost`).
   * Tenant-scoped — any workspace member may read it; a server-fixed rolling window.
   */
  async getWorkspaceDashboard(workspaceId: string): Promise<WorkspaceDashboard> {
    return (await this.get<WorkspaceDashboard>(`/api/workspaces/${workspaceId}/dashboard`)).data;
  }
  /**
   * Operator-only platform stats (cross-tenant): totals, the most-active users + agents,
   * and the platform-wide daily spend trend. The server 403s a non-operator caller. Not
   * workspace-scoped — one deployment-wide view.
   */
  async getPlatformStats(): Promise<PlatformStats> {
    return (await this.get<PlatformStats>('/api/admin/platform-stats')).data;
  }
  /**
   * Operator-only cross-tenant workspace list (#278) for the operator workspace picker:
   * every workspace across ALL tenants, each with its owner's name + email. The server
   * 403s a non-operator caller (the web try-loads then hides the picker on 403). Not
   * workspace-scoped — one deployment-wide list; no secrets cross this boundary (I6).
   */
  async listAdminWorkspaces(): Promise<AdminWorkspace[]> {
    return (await this.get<AdminWorkspace[]>('/api/admin/workspaces')).data;
  }
  /**
   * Operator-only platform-user typeahead (#322) for the Poller Activity email lookup: users whose
   * email or name CONTAINS `q` (case-insensitive), most-relevant first. `limit` is clamped
   * server-side (default 10, max 25). The server 403s a non-operator caller; no secrets cross (I6).
   */
  async searchAdminUsers(q: string, limit?: number): Promise<AdminUserSearchResponse> {
    return (await this.get<AdminUserSearchResponse>('/api/admin/users/search', { q, limit })).data;
  }
  /**
   * Operator-only list of the reporting user's LIVE connections (#322) for the Poller Activity
   * connection dropdown, resolved from their `email` (workspaces + owned mailboxes). Metadata only
   * (id / provider / external account / workspace); no secrets cross (I6). Non-operator → 403.
   */
  async listAdminConnections(email: string): Promise<AdminConnectionsResponse> {
    return (await this.get<AdminConnectionsResponse>('/api/admin/connections', { email })).data;
  }

  // ── Approvals (§10, §14) ──────────────────────────────────────────────────
  async listApprovals(workspaceId: string, query?: Query): Promise<Paginated<ApprovalSummary>> {
    return (await this.get<ApprovalSummary[]>(
      `/api/workspaces/${workspaceId}/approvals`,
      query,
    )) as Paginated<ApprovalSummary>;
  }
  async getApproval(workspaceId: string, approvalId: string): Promise<ApprovalDetail> {
    return (await this.get<ApprovalDetail>(`/api/workspaces/${workspaceId}/approvals/${approvalId}`))
      .data;
  }
  async decideApproval(
    workspaceId: string,
    approvalId: string,
    decision: 'approved' | 'rejected',
    note?: string,
  ): Promise<{ status: string; alreadyDecided: boolean }> {
    return (
      await this.post<{ status: string; alreadyDecided: boolean }>(
        `/api/workspaces/${workspaceId}/approvals/${approvalId}/decision`,
        { decision, note },
      )
    ).data;
  }

  // ── Notifications (§14 badge) ─────────────────────────────────────────────
  async listNotifications(
    workspaceId: string,
    query?: Query,
  ): Promise<Paginated<NotificationView>> {
    return (await this.get<NotificationView[]>(
      `/api/workspaces/${workspaceId}/notifications`,
      query,
    )) as Paginated<NotificationView>;
  }
  async markNotificationRead(workspaceId: string, notificationId: string): Promise<void> {
    await this.request<void>(
      'POST',
      `/api/workspaces/${workspaceId}/notifications/${notificationId}/read`,
    );
  }

  // ── Connections (§14) ─────────────────────────────────────────────────────
  async listConnections(workspaceId: string): Promise<ConnectionView[]> {
    return (await this.get<ConnectionView[]>(`/api/workspaces/${workspaceId}/connections`)).data;
  }
  async connectFakeEmail(workspaceId: string, externalAccount?: string): Promise<ConnectionView> {
    return (
      await this.post<ConnectionView>(`/api/workspaces/${workspaceId}/connections`, {
        provider: 'fake_email',
        externalAccount: externalAccount ?? 'inbox@fake.dev',
      })
    ).data;
  }
  /**
   * Whether the `fake_email` connect affordance should be offered (dev/E2E only).
   * `enabled` is `true` for local development + CI (`NODE_ENV !== 'production'`) and
   * `false` in a deployed production build. The connections screen reads this to HIDE
   * the "Connect fake_email (dev)" button in production while keeping it for local/CI.
   */
  async getFakeEmailConnectStatus(workspaceId: string): Promise<FakeEmailConnectStatus> {
    return (
      await this.get<FakeEmailConnectStatus>(
        `/api/workspaces/${workspaceId}/connections/fake-email/status`,
      )
    ).data;
  }
  /**
   * Whether the Google connect flow is available in this deployment (issue #53).
   * The connections screen reads this to render the Google button enabled or
   * disabled ("not configured") without provoking a 503 on click.
   */
  async getGoogleConnectStatus(workspaceId: string): Promise<GoogleConnectStatus> {
    return (
      await this.get<GoogleConnectStatus>(
        `/api/workspaces/${workspaceId}/connections/google/status`,
      )
    ).data;
  }
  /**
   * Begin the Google OAuth (offline access) connect flow (WP-11). Returns the
   * authorize URL the browser must redirect to, plus the CSRF `state` to echo on
   * the callback. Requires the server to have Google OAuth app credentials
   * configured (dev/CI without them returns a 503 — see the connections screen note).
   */
  async startGoogleConnect(workspaceId: string): Promise<GoogleConnectStartResult> {
    return (
      await this.post<GoogleConnectStartResult>(
        `/api/workspaces/${workspaceId}/connections/google/start`,
      )
    ).data;
  }
  /** Finish the Google OAuth flow with the `code`+`state` from the redirect. */
  async completeGoogleConnect(
    workspaceId: string,
    code: string,
    state: string,
  ): Promise<ConnectionView> {
    return (
      await this.post<ConnectionView>(
        `/api/workspaces/${workspaceId}/connections/google/callback`,
        { code, state },
      )
    ).data;
  }
  /**
   * Whether the HubSpot connect flow is available in this deployment (WP-R6,
   * mirrors #53). The connections screen reads this to render the HubSpot button
   * enabled or disabled ("not configured") without provoking a 503 on click.
   */
  async getHubspotConnectStatus(workspaceId: string): Promise<HubspotConnectStatus> {
    return (
      await this.get<HubspotConnectStatus>(
        `/api/workspaces/${workspaceId}/connections/hubspot/status`,
      )
    ).data;
  }
  /**
   * Begin the HubSpot OAuth connect flow (WP-R6). Returns the authorize URL the
   * browser must redirect to, plus the CSRF `state` to echo on the callback.
   * Requires the server to have HubSpot OAuth app credentials configured (dev/CI
   * without them returns a 503 — see the connections screen note).
   */
  async startHubspotConnect(workspaceId: string): Promise<HubspotConnectStartResult> {
    return (
      await this.post<HubspotConnectStartResult>(
        `/api/workspaces/${workspaceId}/connections/hubspot/start`,
      )
    ).data;
  }
  /** Finish the HubSpot OAuth flow with the `code`+`state` from the redirect. */
  async completeHubspotConnect(
    workspaceId: string,
    code: string,
    state: string,
  ): Promise<ConnectionView> {
    return (
      await this.post<ConnectionView>(
        `/api/workspaces/${workspaceId}/connections/hubspot/callback`,
        { code, state },
      )
    ).data;
  }
  /**
   * Whether the Dispatch (newsletter MCP) connect flow is available (§11.3). The
   * connections screen reads this to enable/disable the "Connect Dispatch" form.
   */
  async getDispatchConnectStatus(workspaceId: string): Promise<DispatchConnectStatus> {
    return (
      await this.get<DispatchConnectStatus>(
        `/api/workspaces/${workspaceId}/connections/dispatch/status`,
      )
    ).data;
  }
  /**
   * Connect the external Dispatch newsletter MCP server (§11.3) by pasting a Personal
   * Access Token (scopes read/write/dispatch). The server validates it live then stores
   * it encrypted (I6); the token crosses the wire only here, never comes back.
   */
  async connectDispatch(workspaceId: string, pat: string): Promise<ConnectionView> {
    return (
      await this.post<ConnectionView>(`/api/workspaces/${workspaceId}/connections/dispatch`, {
        pat,
      })
    ).data;
  }
  /**
   * Whether the PitchReply (MCP) connect flow is available (§11.3, v1.9.1). The
   * connections screen reads this to enable/disable the "Connect PitchReply" form.
   */
  async getPitchreplyConnectStatus(workspaceId: string): Promise<PitchreplyConnectStatus> {
    return (
      await this.get<PitchreplyConnectStatus>(
        `/api/workspaces/${workspaceId}/connections/pitchreply/status`,
      )
    ).data;
  }
  /**
   * Connect the external PitchReply MCP server (§11.3, v1.9.1) by pasting a Personal
   * Access Token. The server validates it live then stores it encrypted (I6); the token
   * crosses the wire only here, never comes back.
   */
  async connectPitchreply(workspaceId: string, pat: string): Promise<ConnectionView> {
    return (
      await this.post<ConnectionView>(`/api/workspaces/${workspaceId}/connections/pitchreply`, {
        pat,
      })
    ).data;
  }
  async testConnection(
    workspaceId: string,
    connectionId: string,
  ): Promise<ConnectionTestResult> {
    return (
      await this.post<ConnectionTestResult>(
        `/api/workspaces/${workspaceId}/connections/${connectionId}/test`,
      )
    ).data;
  }
  async revokeConnection(workspaceId: string, connectionId: string): Promise<void> {
    await this.request<void>(
      'POST',
      `/api/workspaces/${workspaceId}/connections/${connectionId}/revoke`,
    );
  }

  // ── Quick-chat assistant (§21, WP-C4) ─────────────────────────────────────
  /**
   * Load a chat session's persisted turns in causal order (§21; WP-C3). Used when
   * the panel opens to rehydrate an existing conversation. The live turn itself is
   * streamed, not fetched here — see the web `streamChatMessage` helper (fetch +
   * ReadableStream) which the JSON envelope helper can't carry.
   */
  async getChatSessionMessages(
    workspaceId: string,
    sessionId: string,
  ): Promise<ChatMessage[]> {
    return (
      await this.get<ChatMessage[]>(
        `/api/workspaces/${workspaceId}/chat/sessions/${sessionId}/messages`,
      )
    ).data;
  }
  /**
   * Approve or reject an inline chat approval (§10, §21). This unblocks the still-open
   * `/chat/messages` stream — the turn continues on that same connection, so the panel
   * must NOT reopen it. `decision` mirrors the pinned chat contract's verbs.
   */
  async decideChatApproval(
    workspaceId: string,
    approvalId: string,
    decision: 'approve' | 'reject',
  ): Promise<{ ok: true }> {
    return (
      await this.post<{ ok: true }>(
        `/api/workspaces/${workspaceId}/chat/approvals/${approvalId}`,
        { decision },
      )
    ).data;
  }

  /**
   * Mint a short-lived, single-use SSE ticket (§14). `EventSource` can't set an
   * Authorization header, so the stream is opened with `?ticket=` instead — and the
   * 30-day session token NEVER rides the URL (fixes the log-leak finding). The caller
   * mints a ticket, then builds the stream URL with it.
   */
  async mintStreamTicket(workspaceId: string): Promise<{ ticket: string; expiresAt: string }> {
    return (
      await this.post<{ ticket: string; expiresAt: string }>(
        `/api/workspaces/${workspaceId}/stream/ticket`,
      )
    ).data;
  }

  /**
   * Absolute URL for the Operations SSE stream (the browser opens it via EventSource).
   * Carries the short-lived single-use `ticket` — not the session token.
   */
  streamUrl(workspaceId: string, ticket: string, lastEventId?: string): string {
    return this.url(`/api/workspaces/${workspaceId}/stream`, { ticket, lastEventId });
  }
}
